import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from 'src/@/components/ui/switch';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Textarea } from 'src/@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from 'src/@/components/ui/select';
import Iconify from 'src/components/Iconify';

// ----------------------------------------------------------------------

const DEFAULT_TZ = 'America/Sao_Paulo';

function computeOpenNow(schedules, timezone) {
    if (!Array.isArray(schedules) || schedules.length === 0) return false;
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone || DEFAULT_TZ,
            weekday: 'short',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).formatToParts(new Date());
        const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
        const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
        const dayNow = weekdayMap[map.weekday];
        const minutesNow = (parseInt(map.hour, 10) % 24) * 60 + parseInt(map.minute, 10);
        return schedules.some((s) => {
            if (Number(s.day) !== dayNow) return false;
            const [hs, ms] = String(s.startTime || '').split(':').map(Number);
            const [he, me] = String(s.endTime || '').split(':').map(Number);
            if (Number.isNaN(hs) || Number.isNaN(he)) return false;
            const start = hs * 60 + (ms || 0);
            const end = he * 60 + (me || 0);
            return minutesNow >= start && minutesNow < end;
        });
    } catch {
        return false;
    }
}

// ----------------------------------------------------------------------

export default function ChannelWebchatHours({ canal, onChange }) {
    const { t } = useTranslation('settings');
    const businessHours = canal?.config?.businessHours || {};
    const enabled = businessHours.enabled === true;
    const timezone = businessHours.timezone || DEFAULT_TZ;
    const schedules = Array.isArray(businessHours.schedules) ? businessHours.schedules : [];

    const diasSemana = [
        { value: 1, label: t('dates.weekdays.monday', { ns: 'common' }) },
        { value: 2, label: t('dates.weekdays.tuesday', { ns: 'common' }) },
        { value: 3, label: t('dates.weekdays.wednesday', { ns: 'common' }) },
        { value: 4, label: t('dates.weekdays.thursday', { ns: 'common' }) },
        { value: 5, label: t('dates.weekdays.friday', { ns: 'common' }) },
        { value: 6, label: t('dates.weekdays.saturday', { ns: 'common' }) },
        { value: 0, label: t('dates.weekdays.sunday', { ns: 'common' }) },
    ];

    const timezones = [
        { value: 'America/Sao_Paulo', label: t('channels.webchat.hours.tzSaoPaulo', 'Brasília (São Paulo) — UTC-3') },
        { value: 'America/Bahia', label: t('channels.webchat.hours.tzBahia', 'Bahia — UTC-3') },
        { value: 'America/Fortaleza', label: t('channels.webchat.hours.tzFortaleza', 'Fortaleza — UTC-3') },
        { value: 'America/Recife', label: t('channels.webchat.hours.tzRecife', 'Recife — UTC-3') },
        { value: 'America/Belem', label: t('channels.webchat.hours.tzBelem', 'Belém — UTC-3') },
        { value: 'America/Campo_Grande', label: t('channels.webchat.hours.tzCampoGrande', 'Campo Grande — UTC-4') },
        { value: 'America/Cuiaba', label: t('channels.webchat.hours.tzCuiaba', 'Cuiabá — UTC-4') },
        { value: 'America/Manaus', label: t('channels.webchat.hours.tzManaus', 'Manaus — UTC-4') },
        { value: 'America/Porto_Velho', label: t('channels.webchat.hours.tzPortoVelho', 'Porto Velho — UTC-4') },
        { value: 'America/Boa_Vista', label: t('channels.webchat.hours.tzBoaVista', 'Boa Vista — UTC-4') },
        { value: 'America/Rio_Branco', label: t('channels.webchat.hours.tzRioBranco', 'Rio Branco — UTC-5') },
        { value: 'America/Noronha', label: t('channels.webchat.hours.tzNoronha', 'Fernando de Noronha — UTC-2') },
    ];

    const commit = (next) => onChange({ target: { name: 'businessHours.schedules', value: next } });

    const rangesFor = (day) =>
        schedules.map((s, idx) => ({ ...s, idx })).filter((s) => Number(s.day) === day);

    const toggleDay = (day, on) => {
        if (on) commit([...schedules, { day, startTime: '08:00', endTime: '18:00' }]);
        else commit(schedules.filter((s) => Number(s.day) !== day));
    };

    const addRange = (day) => commit([...schedules, { day, startTime: '08:00', endTime: '18:00' }]);
    const updateRange = (idx, patch) => commit(schedules.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
    const removeRange = (idx) => commit(schedules.filter((_, i) => i !== idx));

    const openNow = React.useMemo(
        () => enabled && computeOpenNow(schedules, timezone),
        [enabled, schedules, timezone]
    );

    return (
        <div className="flex flex-col gap-6 mt-2 animate-in fade-in duration-300">
            {/* Ativar */}
            <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 p-4">
                <div>
                    <p className="text-sm font-bold text-gray-900">{t('channels.webchat.hours.title', 'Horário de atendimento')}</p>
                    <p className="text-xs text-gray-500">
                        {t('channels.webchat.hours.subtitle', 'Exibe um aviso de "fora do horário" no chat fora dos períodos configurados.')}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {enabled && (
                        <span
                            className={`text-xs font-medium flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${
                                openNow
                                    ? 'bg-green-50 text-green-700 border-green-200'
                                    : 'bg-gray-50 text-gray-500 border-gray-200'
                            }`}
                        >
                            <span className={`w-1.5 h-1.5 rounded-full ${openNow ? 'bg-green-500' : 'bg-gray-400'}`} />
                            {openNow
                                ? t('channels.webchat.hours.statusOpen', 'Aberto agora')
                                : t('channels.webchat.hours.statusClosed', 'Fechado agora')}
                        </span>
                    )}
                    <Switch
                        checked={enabled}
                        onCheckedChange={(value) => onChange({ target: { name: 'businessHours.enabled', value } })}
                    />
                </div>
            </div>

            {enabled && (
                <>
                    {/* Fuso + mensagem */}
                    <div className="bg-white rounded-xl border border-gray-100 p-5 flex flex-col gap-4">
                        <div className="flex flex-col gap-1.5 md:w-2/3">
                            <Label className="text-xs font-medium text-gray-600">{t('channels.webchat.hours.timezoneLabel', 'Fuso horário')}</Label>
                            <Select
                                value={timezone}
                                onValueChange={(value) => onChange({ target: { name: 'businessHours.timezone', value } })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {timezones.map((tz) => (
                                        <SelectItem key={tz.value} value={tz.value}>
                                            {tz.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <Label className="text-xs font-medium text-gray-600">{t('channels.webchat.hours.outsideMessageLabel', 'Mensagem fora do horário')}</Label>
                            <Textarea
                                rows={3}
                                value={businessHours.outsideMessage || ''}
                                placeholder={t('channels.webchat.hours.outsideMessagePlaceholder', 'Estamos fora do horário de atendimento. Deixe sua mensagem que retornaremos assim que possível.')}
                                onChange={(e) =>
                                    onChange({ target: { name: 'businessHours.outsideMessage', value: e.target.value } })
                                }
                            />
                        </div>
                    </div>

                    {/* Grade semanal */}
                    <div className="bg-white rounded-xl border border-gray-100 p-5 flex flex-col gap-2">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('channels.webchat.hours.weeklyGridTitle', 'Grade semanal')}</p>
                        {diasSemana.map((d) => {
                            const ranges = rangesFor(d.value);
                            const isOpen = ranges.length > 0;
                            return (
                                <div key={d.value} className="flex flex-col gap-2 py-2 border-b border-gray-50 last:border-0">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Switch checked={isOpen} onCheckedChange={(on) => toggleDay(d.value, on)} />
                                            <span className="text-sm text-gray-800">{d.label}</span>
                                        </div>
                                        {isOpen ? (
                                            <button
                                                type="button"
                                                onClick={() => addRange(d.value)}
                                                className="flex items-center gap-1 text-xs font-medium text-[#7C3AED] hover:text-[#6d28d9]"
                                            >
                                                <Iconify icon="eva:plus-outline" width={14} />
                                                {t('channels.webchat.hours.addRangeButton', 'faixa')}
                                            </button>
                                        ) : (
                                            <span className="text-xs text-gray-400">{t('status.closed', { ns: 'common' })}</span>
                                        )}
                                    </div>

                                    {ranges.map((r) => (
                                        <div key={r.idx} className="flex items-center gap-2 pl-11">
                                            <Input
                                                type="time"
                                                value={r.startTime || ''}
                                                onChange={(e) => updateRange(r.idx, { startTime: e.target.value })}
                                                className="w-32"
                                            />
                                            <span className="text-xs text-gray-400">{t('channels.webchat.hours.rangeSeparator', 'às')}</span>
                                            <Input
                                                type="time"
                                                value={r.endTime || ''}
                                                onChange={(e) => updateRange(r.idx, { endTime: e.target.value })}
                                                className="w-32"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeRange(r.idx)}
                                                className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                                                aria-label={t('channels.webchat.hours.removeRangeAriaLabel', 'Remover faixa')}
                                            >
                                                <Iconify icon="eva:close-outline" width={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
